/**
 * Minimal reader for YOM's v2 report API, used for the owner value cards and
 * the live "people on your site now" indicator.
 *
 * WHY NOT `<yom-report>` FOR THOSE: the cards need owner words ("Calls &
 * WhatsApp taps", "Chatbot chats") and numbers combined from several reports;
 * the embed renders one report with YOM's labels. Charts and tables still go
 * through `<yom-report>`. Same token, same report allow-list, same URL shape
 * as the embed's `buildReportUrl` (packages/embed/src/report/api.ts).
 */
import type { EmbedSource, YomReportName } from './types';

export class YomReportError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'YomReportError';
  }
}

export function buildYomReportUrl(
  src: Pick<EmbedSource, 'api' | 'project'>,
  report: YomReportName,
  params: Record<string, string | number | null | undefined> = {}
): string {
  const base = src.api.replace(/\/+$/, '');
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== '') qs.set(k, String(v));
  }
  const q = qs.toString();
  return `${base}/v2/projects/${encodeURIComponent(src.project)}/reports/${report}${q ? `?${q}` : ''}`;
}

/**
 * GET a report. `getToken(rejected?)` supplies the token; on a 401 it is asked
 * once more with the rejected token so the manager mints a fresh one.
 */
export async function fetchYomReport<T>(
  src: Pick<EmbedSource, 'api' | 'project'>,
  report: YomReportName,
  params: Record<string, string | number | null | undefined>,
  getToken: (rejected?: string | null) => Promise<string>,
  signal?: AbortSignal
): Promise<T> {
  const url = buildYomReportUrl(src, report, params);
  const once = async (token: string) => {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      credentials: 'omit',
      signal,
    });
    let body: { success?: boolean; data?: T; error?: { message?: string } } | null = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (!res.ok || !body || body.success === false) {
      throw new YomReportError(
        res.ok ? 500 : res.status,
        body?.error?.message || `Report failed (${res.status})`
      );
    }
    return body.data as T;
  };
  const token = await getToken();
  try {
    return await once(token);
  } catch (err) {
    if (err instanceof YomReportError && err.status === 401) {
      return once(await getToken(token));
    }
    throw err;
  }
}

// Response shapes we read (subset of packages/embed/src/report/types.ts).
export type Num = number | null;
export interface OverviewData {
  kpis: Partial<Record<string, Num>>;
  previous?: Partial<Record<string, Num>> | null;
}
export interface BreakdownData {
  rows: { key: string | null; count: Num }[];
}
export interface RealtimeData {
  active_visitors: Num;
  active_sessions: Num;
  top_pages: { path: string | null; sessions: Num }[];
}
